import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ProductRefSearchAdd.css';

function SearchResultCard({ product, onClick }) {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick(product);
  };

  return (
    <div className="product-ref-search-card" onClick={handleClick}>
      <div className="product-ref-search-card-image">
        <img
          src={product.thumbnail || product.image || 'https://via.placeholder.com/60x60/333/666?text=No+Img'}
          alt={product.name}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/60x60/333/666?text=No+Img';
          }}
        />
      </div>
      <div className="product-ref-search-card-content">
        <h4 className="product-ref-search-card-title">{product.name}</h4>
        <div className="product-ref-search-card-meta">
          <span className="product-ref-search-card-price">${product.price}</span>
          {product.tags && product.tags.length > 0 && (
            <span className="product-ref-search-card-tags">
              {product.tags.slice(0, 2).join(', ')}
            </span>
          )}
        </div>
      </div>
      <div className="product-ref-search-card-arrow">
        <i className="fa-solid fa-plus"></i>
      </div>
    </div>
  );
}

export function ProductRefSearchAdd({ manager, onSelect, placeholder = 'Search products...', excludeIds = [], excludeCurrentId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const excludeSet = useCallback(() => {
    const set = new Set(Array.isArray(excludeIds) ? excludeIds : []);
    if (excludeCurrentId) set.add(excludeCurrentId);
    return set;
  }, [excludeIds, excludeCurrentId]);

  const performSearch = useCallback(async (q) => {
    if (!manager || !manager.searchProductsFromAdminAPI) return;
    const trimmed = (q || '').trim();
    if (!trimmed) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const { products = [] } = await manager.searchProductsFromAdminAPI(trimmed, 1, 10);
      const ex = excludeSet();
      const filtered = products.filter((p) => !ex.has(p.id));
      setResults(filtered);
      setIsOpen(filtered.length > 0);
    } catch (err) {
      console.error('ProductRefSearchAdd search failed:', err);
      setResults([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }, [manager, excludeSet]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (product) => {
    if (onSelect) onSelect(product);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div className="product-ref-search-add" ref={containerRef}>
      <div className="product-ref-search-container">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="product-ref-search-input"
        />
        {loading && (
          <span className="product-ref-search-spinner">
            <i className="fa-solid fa-spinner fa-spin"></i>
          </span>
        )}
      </div>
      {isOpen && results.length > 0 && (
        <div className="product-ref-search-dropdown">
          {results.map((p) => (
            <SearchResultCard key={p.id} product={p} onClick={handleSelect} />
          ))}
        </div>
      )}
    </div>
  );
}
